import React, { useMemo, useState } from 'react';
import { Icon } from '../../design/Icon';

const cellValue = (column, row) => (typeof column.value === 'function' ? column.value(row) : row[column.key]);

const compare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en-US', { numeric: true });
};

/**
 * Sticky-header table from the mockup inventory (.table-wrap/.table).
 * columns: [{ key, header, numeric, tight, sortable, value(row), render(row), exportValue(row) }]
 * The same columns drive CSV export through useExport({ columns, rows }).
 */
export function DataTable({
  columns,
  rows,
  rowKey = (row, i) => row.id ?? i,
  onRowClick,
  selectedKey,
  compact = false,
  maxHeight,
  caption,
  footer,
  initialSort,
}) {
  const [sort, setSort] = useState(initialSort || null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;
    const sorted = [...rows].sort((a, b) => compare(cellValue(column, a), cellValue(column, b)));
    return sort.dir === 'desc' ? sorted.reverse() : sorted;
  }, [rows, columns, sort]);

  const toggleSort = (column) => {
    if (!column.sortable) return;
    setSort((current) =>
      current && current.key === column.key ? { key: column.key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key: column.key, dir: column.numeric ? 'desc' : 'asc' }
    );
  };

  return (
    <>
      <div className="table-wrap" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
        <table className={`table${compact ? ' compact' : ''}`}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((column) => {
                const sorted = sort && sort.key === column.key;
                const cls = [column.numeric ? 'n' : '', column.tight ? 'tight' : ''].filter(Boolean).join(' ') || undefined;
                return (
                  <th
                    key={column.key}
                    className={cls}
                    scope="col"
                    aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    onClick={column.sortable ? () => toggleSort(column) : undefined}
                    style={column.sortable ? { cursor: 'pointer' } : undefined}
                  >
                    {column.header}
                    {column.sortable && <Icon name={sorted && sort.dir === 'asc' ? 'chevU' : 'chevD'} className="ico-sm sort" />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const key = rowKey(row, i);
              const cls = [onRowClick ? 'clickable' : '', selectedKey != null && selectedKey === key ? 'selected' : ''].filter(Boolean).join(' ') || undefined;
              return (
                <tr
                  key={key}
                  className={cls}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter') onRowClick(row);
                        }
                      : undefined
                  }
                >
                  {columns.map((column) => (
                    <td key={column.key} className={[column.numeric ? 'n' : '', column.tight ? 'tight' : ''].filter(Boolean).join(' ') || undefined}>
                      {column.render ? column.render(row) : cellValue(column, row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer && <div className="table-foot">{footer}</div>}
    </>
  );
}

export function Pager({ page, pageCount, onChange }) {
  return (
    <div className="pager">
      <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Previous
      </button>
      <button type="button" className="btn btn-sm" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
        Next
      </button>
    </div>
  );
}

export function BarCell({ value, max, color, label }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="bar-cell">
      <div className="track">
        <div className="fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {label != null && <span className="small num">{label}</span>}
    </div>
  );
}

export function CellTitle({ title, sub }) {
  return (
    <>
      <div className="cell-title">{title}</div>
      {sub && <div className="cell-sub">{sub}</div>}
    </>
  );
}
